/**
 * The forecast grid's column catalogue (f11.1, BR-F11.1-3).
 *
 * ONE ordered list, keyed by the API's field names -- never by a spreadsheet
 * column. The table renders headers, bands and cells from this list alone, so
 * reordering an entry here reorders the grid with no other change.
 *
 * `kind` decides what a cell may be:
 * - `identity`   -- read-only Kimble data, always plain text;
 * - `editable`   -- one of the band-1 fields the server accepts;
 * - `calculated` -- a stored engine output, always plain text.
 */

export type ColumnGroup =
  | 'identity'
  | 'forecast'
  | 'headcount'
  | 'fy'
  | 'fixedService'
  | 'revenueAdjustment'
  | 'totals';

export type ColumnKind = 'identity' | 'editable' | 'calculated';

export type ColumnFormat = 'text' | 'mono' | 'decimal1' | 'decimal2' | 'money' | 'probability' | 'flag' | 'lookup';

/** Line-level fields, as the API names them. */
export type LineField =
  | 'element_code'
  | 'country_code'
  | 'program_name'
  | 'account_name'
  | 'opportunity_name'
  | 'delivery_manager_name'
  | 'opportunity_owner_name'
  | 'product'
  | 'location_name'
  | 'status'
  | 'stage'
  | 'sales_rate_override'
  | 'probability'
  | 'overwrite_fixed_service'
  | 'result.fy_avg_headcount'
  | 'result.fy_fixed_service'
  | 'result.fy_revenue_adjustment'
  | 'result.fy_revenue'
  | 'result.unweighted_revenue';

/** Month-level fields: the three editable inputs. */
export type MonthField = 'months.headcount' | 'months.fixed_service_amount' | 'months.revenue_adjustment';

export interface ForecastColumn {
  id: string;
  field: LineField | MonthField;
  header: string;
  group: ColumnGroup;
  kind: ColumnKind;
  format: ColumnFormat;
  /** 1-12 for a month column. */
  month?: number;
}

export const COLUMN_GROUP_LABELS: Record<ColumnGroup, string> = {
  identity: 'Sales and account information · read-only from Kimble',
  forecast: 'Forecast input',
  headcount: 'Monthly headcount (FTE)',
  fy: 'FY',
  fixedService: 'Fixed service',
  revenueAdjustment: 'Revenue adjustments',
  totals: 'Totals',
};

export const MONTH_HEADERS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthly(field: MonthField, group: ColumnGroup, format: ColumnFormat): ForecastColumn[] {
  return MONTH_HEADERS.map((header, index) => ({
    id: `${field}.${index + 1}`,
    field,
    header,
    group,
    kind: 'editable' as const,
    format,
    month: index + 1,
  }));
}

export const FORECAST_COLUMNS: ForecastColumn[] = [
  { id: 'element_code', field: 'element_code', header: 'Element', group: 'identity', kind: 'identity', format: 'mono' },
  { id: 'country_code', field: 'country_code', header: 'Country', group: 'identity', kind: 'identity', format: 'mono' },
  { id: 'program_name', field: 'program_name', header: 'Program', group: 'identity', kind: 'identity', format: 'text' },
  { id: 'account_name', field: 'account_name', header: 'Account', group: 'identity', kind: 'identity', format: 'text' },
  { id: 'opportunity_name', field: 'opportunity_name', header: 'Project', group: 'identity', kind: 'identity', format: 'text' },
  {
    id: 'delivery_manager_name',
    field: 'delivery_manager_name',
    header: 'Delivery manager',
    group: 'identity',
    kind: 'identity',
    format: 'text',
  },
  {
    id: 'opportunity_owner_name',
    field: 'opportunity_owner_name',
    header: 'Sales opp owner',
    group: 'identity',
    kind: 'identity',
    format: 'text',
  },
  { id: 'product', field: 'product', header: 'Type', group: 'identity', kind: 'identity', format: 'text' },
  { id: 'location_name', field: 'location_name', header: 'Location', group: 'identity', kind: 'identity', format: 'text' },

  { id: 'status', field: 'status', header: 'Status', group: 'forecast', kind: 'editable', format: 'lookup' },
  { id: 'stage', field: 'stage', header: 'Stage', group: 'forecast', kind: 'editable', format: 'lookup' },
  {
    id: 'sales_rate_override',
    field: 'sales_rate_override',
    header: 'Sales rate',
    group: 'forecast',
    kind: 'editable',
    format: 'money',
  },
  { id: 'probability', field: 'probability', header: 'Probability', group: 'forecast', kind: 'editable', format: 'probability' },

  ...monthly('months.headcount', 'headcount', 'decimal1'),
  {
    id: 'fy_avg_headcount',
    field: 'result.fy_avg_headcount',
    header: 'Avg HC',
    group: 'fy',
    kind: 'calculated',
    format: 'decimal2',
  },

  {
    id: 'overwrite_fixed_service',
    field: 'overwrite_fixed_service',
    header: 'Override?',
    group: 'fixedService',
    kind: 'editable',
    format: 'flag',
  },
  ...monthly('months.fixed_service_amount', 'fixedService', 'money'),
  {
    id: 'fy_fixed_service',
    field: 'result.fy_fixed_service',
    header: 'Total managed svc',
    group: 'fixedService',
    kind: 'calculated',
    format: 'money',
  },

  ...monthly('months.revenue_adjustment', 'revenueAdjustment', 'money'),
  {
    id: 'fy_revenue_adjustment',
    field: 'result.fy_revenue_adjustment',
    header: 'FY adjustments',
    group: 'revenueAdjustment',
    kind: 'calculated',
    format: 'money',
  },

  { id: 'fy_revenue', field: 'result.fy_revenue', header: 'FY revenue', group: 'totals', kind: 'calculated', format: 'money' },
  {
    id: 'unweighted_revenue',
    field: 'result.unweighted_revenue',
    header: 'Unweighted',
    group: 'totals',
    kind: 'calculated',
    format: 'money',
  },
];

export interface ColumnBand {
  group: ColumnGroup;
  label: string;
  span: number;
}

/** Consecutive columns of the same group become one header band. */
export function columnBands(columns: readonly ForecastColumn[]): ColumnBand[] {
  const bands: ColumnBand[] = [];
  for (const column of columns) {
    const last = bands[bands.length - 1];
    if (last && last.group === column.group) {
      last.span += 1;
    } else {
      bands.push({ group: column.group, label: COLUMN_GROUP_LABELS[column.group], span: 1 });
    }
  }
  return bands;
}
